import { useCallback, useEffect, useState } from 'react';
import {
  activateManufacturingEquipment,
  deactivateManufacturingEquipment,
  deleteManufacturingEquipment,
  fetchManufacturingEquipment,
  fetchManufacturingEquipmentImageUrl,
} from '../../api/manufacturingEquipment';
import { isEquipmentInUse, ManufacturingEquipment, EquipmentType } from '../../types/manufacturingEquipment';
import ImageLightbox from '../common/ImageLightbox';
import EquipTabBar from './EquipTabBar';
import { EquipTabKey } from './equipTabConfig';
import EquipUseStatus from './EquipUseStatus';
import MainPartsTab from './MainPartsTab';
import StockPartsTab from './StockPartsTab';
import CalibrationsTab from './CalibrationsTab';
import RepairsTab from './RepairsTab';
import ManagementStandardTab from './ManagementStandardTab';
import InspectionSheetTab from './InspectionSheetTab';
import './EquipUseStatus.css';
import '../contract/ContractDetail.css';
import '../product/ProductForm.css';

import { reportError } from '../../utils/notify';
interface ManufacturingEquipDetailProps {
  equipmentId: number;
  equipmentType?: EquipmentType;
  onBack: () => void;
  onEdit: () => void;
  onStatusChanged: () => void;
  onDeleted?: () => void;
}

function ManufacturingEquipDetail({
  equipmentId,
  equipmentType = 'MANUFACTURING',
  onBack,
  onEdit,
  onStatusChanged,
  onDeleted,
}: ManufacturingEquipDetailProps) {
  const [equipment, setEquipment] = useState<ManufacturingEquipment | null>(null);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toggling, setToggling] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [activeTab, setActiveTab] = useState<EquipTabKey>('general');
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const closeLightbox = useCallback(() => setLightboxOpen(false), []);

  useEffect(() => {
    setActiveTab('general');
    let objectUrl: string | null = null;
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        const data = await fetchManufacturingEquipment(equipmentId, equipmentType);
        if (cancelled) return;
        setEquipment(data);
        if (data.hasImage) {
          objectUrl = await fetchManufacturingEquipmentImageUrl(equipmentId, equipmentType);
          if (!cancelled) setImageUrl(objectUrl);
        } else {
          setImageUrl(null);
        }
        setError('');
      } catch {
        if (!cancelled) {
          setEquipment(null);
          reportError(setError, '설비를 불러오지 못했습니다.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
      if (objectUrl) window.URL.revokeObjectURL(objectUrl);
    };
  }, [equipmentId, equipmentType]);

  const handleToggleUse = async () => {
    if (!equipment) return;
    const currentlyInUse = isEquipmentInUse(equipment.useYn);
    if (currentlyInUse) {
      const reason = window.prompt(
        `'${equipment.equipmentName}' 설비를 미사용 처리합니다.\n사유를 입력해 주세요.`
      );
      if (reason === null) return;
      if (!reason.trim()) {
        reportError(setError, '미사용 사유를 입력해 주세요.');
        return;
      }
      try {
        setToggling(true);
        const updated = await deactivateManufacturingEquipment(
          equipment.equipmentId,
          reason.trim(),
          equipmentType
        );
        setEquipment(updated);
        setError('');
        onStatusChanged();
      } catch (err) {
        const message =
          (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          '미사용 처리에 실패했습니다.';
        reportError(setError, message);
      } finally {
        setToggling(false);
      }
      return;
    }

    const ok = window.confirm(`'${equipment.equipmentName}' 설비를 다시 사용 처리할까요?`);
    if (!ok) return;
    try {
      setToggling(true);
      const updated = await activateManufacturingEquipment(equipment.equipmentId, equipmentType);
      setEquipment(updated);
      setError('');
      onStatusChanged();
    } catch {
      reportError(setError, '사용 처리에 실패했습니다.');
    } finally {
      setToggling(false);
    }
  };

  const handleDelete = async () => {
    if (!equipment) return;
    const label = equipmentType === 'INSPECTION' ? '검사설비' : '제조설비';
    const ok = window.confirm(
      `'${equipment.equipmentName}' ${label}를 삭제할까요?\n\n교정·수리·점검시트·관리기준·첨부 등 관련 데이터가 함께 삭제되며 되돌릴 수 없습니다.`
    );
    if (!ok) return;
    try {
      setDeleting(true);
      await deleteManufacturingEquipment(equipment.equipmentId, equipmentType);
      onDeleted?.();
    } catch (err) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        '설비 삭제에 실패했습니다.';
      reportError(setError, message);
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return <p className="contract-detail__hint">불러오는 중...</p>;
  }

  if (!equipment) {
    return (
      <div className="contract-detail">
        <p className="contract-detail__error">{error || '설비를 찾을 수 없습니다.'}</p>
        <div className="contract-detail__actions">
          <button type="button" className="is-ghost" onClick={onBack}>
            목록으로
          </button>
        </div>
      </div>
    );
  }

  const inUse = isEquipmentInUse(equipment.useYn);

  return (
    <div className="contract-detail">
      <header className="contract-detail__header">
        <div className="contract-detail__header-top">
          <div className="product-detail__hero">
            <div
              className={`product-detail__thumb${imageUrl ? ' image-lightbox-trigger' : ''}`}
              role={imageUrl ? 'button' : undefined}
              tabIndex={imageUrl ? 0 : undefined}
              title={imageUrl ? '클릭하여 확대' : undefined}
              onClick={() => {
                if (imageUrl) setLightboxOpen(true);
              }}
              onKeyDown={(e) => {
                if (!imageUrl) return;
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setLightboxOpen(true);
                }
              }}
            >
              {imageUrl ? (
                <img src={imageUrl} alt={equipment.equipmentName} />
              ) : (
                <span>사진 없음</span>
              )}
            </div>
            <div>
              <h2>{equipment.equipmentName}</h2>
              <p>
                {equipment.equipmentCode || '설비번호 없음'}
                {equipment.manufacturer ? ` · ${equipment.manufacturer}` : ''}
                {equipment.managerName ? ` · 담당 ${equipment.managerName}` : ''}
              </p>
              <div className="product-detail__badges" style={{ marginTop: 8 }}>
                <EquipUseStatus useYn={equipment.useYn} />
                {equipmentType === 'INSPECTION' && equipment.ownershipType === 'RENTAL' && (
                  <span className="equip-calib-status is-none" style={{ marginLeft: 6 }}>
                    대여
                  </span>
                )}
                {equipmentType === 'INSPECTION' && equipment.calibStatus && equipment.calibStatus !== 'NONE' && (
                  <span
                    className={`equip-calib-status is-${equipment.calibStatus.toLowerCase()}`}
                    style={{ marginLeft: 6 }}
                  >
                    {equipment.calibStatus === 'EXPIRED'
                      ? '교정 만료'
                      : equipment.calibStatus === 'EXPIRING'
                        ? '교정 만료 임박'
                        : '교정 유효'}
                    {equipment.latestCalibValidDate ? ` (${equipment.latestCalibValidDate})` : ''}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="contract-detail__actions">
            <button type="button" className="is-ghost" onClick={onEdit}>
              수정
            </button>
            <button
              type="button"
              className={inUse ? 'is-danger' : 'is-ghost'}
              onClick={() => void handleToggleUse()}
              disabled={toggling || deleting}
            >
              {toggling ? '처리 중...' : inUse ? '미사용' : '사용'}
            </button>
            <button
              type="button"
              className="is-danger"
              onClick={() => void handleDelete()}
              disabled={deleting || toggling}
            >
              {deleting ? '삭제 중...' : '삭제'}
            </button>
            <button type="button" className="is-ghost" onClick={onBack}>
              목록으로
            </button>
          </div>
        </div>
      </header>

      {error && <p className="contract-detail__error">{error}</p>}

      <EquipTabBar activeTab={activeTab} onChange={setActiveTab} />

      {activeTab === 'general' ? (
        <div className="contract-detail__grid">
          <section className="contract-detail__card">
            <h3>기본 정보</h3>
            <dl>
              <div>
                <dt>설비 번호</dt>
                <dd>{equipment.equipmentCode || '-'}</dd>
              </div>
              <div>
                <dt>설비 명</dt>
                <dd>{equipment.equipmentName}</dd>
              </div>
              <div>
                <dt>제작사</dt>
                <dd>{equipment.manufacturer || '-'}</dd>
              </div>
              <div>
                <dt>설치 일자</dt>
                <dd>{equipment.installDate || '-'}</dd>
              </div>
              <div>
                <dt>관리 담당자</dt>
                <dd>{equipment.managerName || '-'}</dd>
              </div>
              <div>
                <dt>위치</dt>
                <dd>{equipment.location || '-'}</dd>
              </div>
              {equipmentType === 'INSPECTION' && (
                <>
                  <div>
                    <dt>소유 구분</dt>
                    <dd>{equipment.ownershipType === 'RENTAL' ? '대여' : '자체 보유'}</dd>
                  </div>
                  {equipment.ownershipType === 'RENTAL' && (
                    <>
                      <div>
                        <dt>대여처</dt>
                        <dd>{equipment.rentalVendor || '-'}</dd>
                      </div>
                      <div>
                        <dt>대여 기간</dt>
                        <dd>
                          {equipment.rentalStartDate || '-'} ~ {equipment.rentalEndDate || '-'}
                        </dd>
                      </div>
                    </>
                  )}
                  <div>
                    <dt>교정 주기</dt>
                    <dd>
                      {equipment.calibCycleMonths ? `${equipment.calibCycleMonths}개월` : '-'}
                    </dd>
                  </div>
                  <div>
                    <dt>만료 임박 알림</dt>
                    <dd>
                      {equipment.calibWarnDays != null ? `${equipment.calibWarnDays}일 전` : '-'}
                    </dd>
                  </div>
                  <div>
                    <dt>최근 교정 유효일</dt>
                    <dd>{equipment.latestCalibValidDate || '-'}</dd>
                  </div>
                </>
              )}
              <div>
                <dt>사용</dt>
                <dd>
                  <EquipUseStatus useYn={equipment.useYn} />
                </dd>
              </div>
              {!inUse && (
                <>
                  <div>
                    <dt>미사용 처리자</dt>
                    <dd>{equipment.deactivatedUserName || '-'}</dd>
                  </div>
                  <div>
                    <dt>미사용 일시</dt>
                    <dd>
                      {equipment.deactivatedAt
                        ? new Date(equipment.deactivatedAt).toLocaleString('ko-KR')
                        : '-'}
                    </dd>
                  </div>
                  <div>
                    <dt>미사용 사유</dt>
                    <dd>{equipment.deactivateReason || '-'}</dd>
                  </div>
                </>
              )}
            </dl>
          </section>
          <section className="contract-detail__card">
            <h3>제원 및 사양</h3>
            <dl>
              <div>
                <dt>성능</dt>
                <dd>{equipment.performance || '-'}</dd>
              </div>
              <div>
                <dt>동력</dt>
                <dd>{equipment.powerKw ? `${equipment.powerKw} kW` : '-'}</dd>
              </div>
              <div>
                <dt>전원</dt>
                <dd>{equipment.voltage || '-'}</dd>
              </div>
            </dl>
          </section>
          {equipment.memo && (
            <section className="contract-detail__card is-full">
              <h3>비고</h3>
              <p className="contract-detail__memo">{equipment.memo}</p>
            </section>
          )}
        </div>
      ) : activeTab === 'mainParts' ? (
        <MainPartsTab equipmentId={equipmentId} />
      ) : activeTab === 'stockParts' ? (
        <StockPartsTab />
      ) : activeTab === 'calibrations' ? (
        <CalibrationsTab
          equipmentId={equipmentId}
          equipmentType={equipmentType}
          calibCycleMonths={equipment.calibCycleMonths}
          calibWarnDays={equipment.calibWarnDays}
        />
      ) : activeTab === 'repairs' ? (
        <RepairsTab equipmentId={equipmentId} />
      ) : activeTab === 'managementStandard' ? (
        <ManagementStandardTab equipmentId={equipmentId} equipmentType={equipmentType} />
      ) : (
        <InspectionSheetTab equipmentId={equipmentId} />
      )}
      <ImageLightbox
        src={lightboxOpen ? imageUrl : null}
        alt={equipment.equipmentName}
        onClose={closeLightbox}
      />
    </div>
  );
}

export default ManufacturingEquipDetail;
